import React, { useEffect, useState } from 'react';
import api from "../services/api";
import { jwtDecode } from "jwt-decode"; // 👈 1. IMPORTAR

const InventarioFisico = () => {
  const [form, setForm] = useState({ 
    codigo: '', 
    descripcion: '', 
    cantidad_fisica: '', 
    responsable: '', 
    observaciones: '' 
  });
  const [busqueda, setBusqueda] = useState('');
  const [sugerencias, setSugerencias] = useState([]);
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState({ texto: "", tipo: "" });
  const [editando, setEditando] = useState(null);
  const [usuario, setUsuario] = useState(null); // 👈 2. AÑADIR ESTADO DE USUARIO

  useEffect(() => {
    cargarRegistros();

    // 3. 🔽 OBTENER USUARIO LOGUEADO Y PONERLO POR DEFECTO
    const getUsuario = () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const decoded = jwtDecode(token);
          setUsuario(decoded);
          // Poner el nombre por defecto en el formulario
          const userName = decoded.nombre || decoded.email || 'Usuario (Conteo)';
          setForm(prevForm => ({ ...prevForm, responsable: userName }));
        } catch (error) {
          console.error("Error al decodificar token:", error);
        }
      }
    };
    getUsuario();
    // 3. 🔼 FIN DEL BLOQUE

  }, []); // Cargar solo una vez

  const cargarRegistros = async () => {
    try {
      setLoading(true);
      const res = await api.get('/inventario-fisico');
      setRegistros(res.data);
    } catch (error) {
      console.error('Error al cargar registros:', error);
      setMensaje({ texto: "❌ Error al cargar registros", tipo: "error" });
    } finally {
      setLoading(false);
    }
  };

  const buscarMateriales = async (texto) => {
    setBusqueda(texto);
    if (texto.length < 2) {
      setSugerencias([]);
      return;
    }
    try {
      const res = await api.get(`/materiales/buscar?q=${texto}`);
      setSugerencias(res.data);
    } catch (error) {
      console.error("Error al buscar materiales:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.codigo || !form.cantidad_fisica) {
      setMensaje({ texto: "⚠️ Código y cantidad son obligatorios", tipo: "error" });
      return;
    }

    try {
      setLoading(true);
      
      // Asegurar que el responsable (del estado 'form') se envía
      const payload = { ...form };
      
      if (editando) {
        await api.put(`/inventario-fisico/${editando}`, payload);
        setMensaje({ texto: "✅ Registro actualizado exitosamente", tipo: "success" });
      } else {
        await api.post('/inventario-fisico', payload);
        setMensaje({ texto: "✅ Registro guardado exitosamente", tipo: "success" });
      }
      
      // 5. 🔽 RESETEAR FORMULARIO (MANTENIENDO AL USUARIO)
      const userName = usuario?.nombre || usuario?.email || 'Usuario (Conteo)';
      setForm({ 
        codigo: '', 
        descripcion: '', 
        cantidad_fisica: '', 
        responsable: userName, // <-- Poner el usuario por defecto
        observaciones: '' 
      });
      setBusqueda('');
      setEditando(null);
      cargarRegistros();
      
      setTimeout(() => setMensaje({ texto: "", tipo: "" }), 3000);
    } catch (error) {
      console.error('Error al guardar:', error);
      setMensaje({ texto: "❌ Error al guardar el registro", tipo: "error" });
    } finally {
      setLoading(false);
    }
  };

  const editar = (registro) => {
    setForm({
      codigo: registro.codigo,
      descripcion: registro.descripcion || '',
      cantidad_fisica: registro.cantidad_fisica,
      responsable: registro.responsable || '', // Carga el responsable guardado
      observaciones: registro.observaciones || ''
    });
    setBusqueda(registro.codigo);
    setEditando(registro.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelarEdicion = () => {
    // 6. 🔽 RESETEAR FORMULARIO AL CANCELAR (MANTENIENDO AL USUARIO)
    const userName = usuario?.nombre || usuario?.email || 'Usuario (Conteo)';
    setForm({ 
      codigo: '', 
      descripcion: '', 
      cantidad_fisica: '', 
      responsable: userName, // <-- Poner el usuario por defecto
      observaciones: '' 
    });
    setBusqueda('');
    setEditando(null);
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este registro?')) return;
    
    try {
      setLoading(true);
      await api.delete(`/inventario-fisico/${id}`);
      setMensaje({ texto: "✅ Registro eliminado exitosamente", tipo: "success" });
      cargarRegistros();
      setTimeout(() => setMensaje({ texto: "", tipo: "" }), 3000);
    } catch (error) {
      console.error('Error al eliminar:', error);
      setMensaje({ texto: "❌ Error al eliminar el registro", tipo: "error" });
    } finally {
      setLoading(false);
    }
  };

  const generarChecklist = async () => {
    try {
      setLoading(true);
      const response = await api.get('/inventario-fisico/checklist/excel', {
        responseType: 'blob'
      });
      
      // Crear un link de descarga
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Checklist_Inventario_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setMensaje({ texto: "✅ Checklist generado exitosamente", tipo: "success" });
      setTimeout(() => setMensaje({ texto: "", tipo: "" }), 3000);
    } catch (error) {
      console.error('Error al generar checklist:', error);
      setMensaje({ texto: "❌ Error al generar el checklist", tipo: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      {/* Header con botón de checklist */}
      <div style={styles.headerContainer}>
        {/* ... (código del header sin cambios) ... */}
         <div>
          <h1 style={styles.title}>📦 Inventario Físico</h1>
          <p style={styles.subtitle}>Registro y control de conteos físicos de inventario</p>
        </div>
        <button 
          style={styles.btnChecklist}
          onClick={generarChecklist}
          disabled={loading}
          onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
        >
          📋 Generar Checklist Excel
        </button>
      </div>

      {/* Formulario */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>
          {editando ? "✏️ Editar Registro" : "➕ Nuevo Registro de Conteo"}
        </h3>
        
        <form onSubmit={handleSubmit}>
          <div style={styles.formGrid}>
            
            {/* Campo con búsqueda dinámica */}
            <div style={{ position: 'relative' }}>
              <label style={styles.label}>📋 Código del Material *</label>
              <input
                type="text"
                style={styles.input}
                placeholder="Escribe código o nombre..."
                value={busqueda}
                onChange={(e) => buscarMateriales(e.target.value)}
                required
              />
              {sugerencias.length > 0 && (
                <ul style={styles.sugerencias}>
                  {sugerencias.map((m, idx) => (
                    <li
                      key={idx}
                      style={styles.sugerenciaItem}
                      onClick={() => {
                        // 4. 🔽 CORRECCIÓN: Ya no se pone "Usuario actual"
                        setForm({
                          ...form, // 'responsable' ya está en el 'form'
                          codigo: m.codigo,
                          descripcion: m.nombre,
                          // responsable: "Usuario actual", // 👈 LÍNEA ELIMINADA
                        });
                        setBusqueda(m.codigo);
                        setSugerencias([]);
                      }}
                    >
                      {m.codigo} — {m.nombre}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>📝 Descripción</label>
              <input
                type="text"
                style={styles.input}
                placeholder="Descripción del material"
                value={form.descripcion}
                onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>🔢 Cantidad Física *</label>
              <input
                type="number"
                style={styles.input}
                placeholder="0"
                value={form.cantidad_fisica}
                onChange={(e) => setForm({ ...form, cantidad_fisica: e.target.value })}
                required
                min="0"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>👤 Responsable</label>
              <input
                type="text"
                style={styles.input}
                placeholder="Responsable del conteo"
                value={form.responsable} // 👈 AHORA VIENE DEL ESTADO
                onChange={(e) => setForm({ ...form, responsable: e.target.value })}
              />
            </div>

            <div style={{...styles.formGroup, gridColumn: '1 / -1'}}>
              <label style={styles.label}>📄 Observaciones</label>
              <textarea
                style={{...styles.input, minHeight: '80px', resize: 'vertical'}}
                placeholder="Notas o comentarios..."
                value={form.observaciones}
                onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
              />
            </div>
          </div>

          <div style={styles.formActions}>
            <button 
              type="submit" 
              style={styles.btnGuardar}
              disabled={loading}
            >
              {loading ? "⏳ Guardando..." : editando ? "💾 Actualizar" : "💾 Guardar"}
            </button>
            
            {editando && (
              <button 
                type="button" 
                style={styles.btnCancelar}
                onClick={cancelarEdicion}
              >
                ❌ Cancelar
              </button>
            )}
          </div>
        </form>

        {mensaje.texto && (
          <div
            style={{
              ...styles.message,
              ...(mensaje.tipo === "success" ? styles.messageSuccess : styles.messageError),
            }}
          >
            {mensaje.texto}
          </div>
        )}
      </div>

      {/* Tabla de registros (sin cambios) */}
      <div style={styles.card}>
        {/* ... (código de la tabla de registros sin cambios) ... */}
        <h3 style={styles.cardTitle}>📋 Registros de Conteo Físico</h3>
        
        {loading && !registros.length ? (
          <div style={styles.loadingMessage}>⏳ Cargando registros...</div>
        ) : registros.length === 0 ? (
          <div style={styles.emptyMessage}>
            📭 No hay registros de inventario físico
          </div>
        ) : (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeader}>
                  <th style={styles.th}>Código</th>
                  <th style={styles.th}>Descripción</th>
                  <th style={styles.th}>Cantidad Física</th>
                  <th style={styles.th}>Fecha Conteo</th>
                  <th style={styles.th}>Responsable</th>
                  <th style={styles.th}>Observaciones</th>
                  <th style={styles.th}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {registros.map((registro, idx) => (
                  <tr 
                    key={registro.id} 
                    style={idx % 2 === 0 ? styles.trEven : styles.trOdd}
                  >
                    <td style={styles.td}><strong>{registro.codigo}</strong></td>
                    <td style={styles.td}>{registro.descripcion || '-'}</td>
                    <td style={styles.tdNum}>
                      <span style={styles.badge}>{registro.cantidad_fisica}</span>
                    </td>
                    <td style={styles.td}>
                      {new Date(registro.fecha_conteo).toLocaleDateString('es-DO')}
                    </td>
                    <td style={styles.td}>{registro.responsable || '-'}</td>
                    <td style={styles.td}>{registro.observaciones || '-'}</td>
                    <td style={styles.tdActions}>
                      <button 
                        style={styles.btnEditar}
                        onClick={() => editar(registro)}
                        title="Editar"
                      >
                        ✏️
                      </button>
                      <button 
                        style={styles.btnEliminar}
                        onClick={() => eliminar(registro.id)}
                        title="Eliminar"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ... (Pega todos tus estilos aquí sin cambios)
const styles = {
  container: {
    minHeight: '100vh',
    background: '#f8fafc',
    padding: '20px',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '25px',
    flexWrap: 'wrap',
    gap: '15px',
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 5px 0',
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: '0',
  },
  btnChecklist: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '30px',
    marginBottom: '20px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    border: '1px solid #e2e8f0',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 25px 0',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '25px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#475569',
    marginBottom: '8px',
  },
  input: {
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  sugerencias: {
    position: 'absolute',
    zIndex: 10,
    background: 'white',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    marginTop: '4px',
    maxHeight: '200px',
    overflowY: 'auto',
    width: '100%',
    listStyle: 'none',
    padding: '0',
    margin: '0',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  },
  sugerenciaItem: {
    padding: '10px 12px',
    cursor: 'pointer',
    borderBottom: '1px solid #e2e8f0',
    transition: 'background 0.2s',
  },
  formActions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-start',
  },
  btnGuardar: {
    padding: '12px 32px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
    transition: 'transform 0.2s',
  },
  btnCancelar: {
    padding: '12px 32px',
    background: '#64748b',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'transform 0.2s',
  },
  message: {
    marginTop: '20px',
    padding: '12px 20px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    textAlign: 'center',
  },
  messageSuccess: {
    background: '#d1fae5',
    color: '#065f46',
    border: '1px solid #10b981',
  },
  messageError: {
    background: '#fee2e2',
    color: '#991b1b',
    border: '1px solid #ef4444',
  },
  loadingMessage: {
    textAlign: 'center',
    padding: '40px',
    fontSize: '16px',
    color: '#64748b',
  },
  emptyMessage: {
    textAlign: 'center',
    padding: '40px',
    fontSize: '16px',
    color: '#64748b',
    background: '#f8fafc',
    borderRadius: '8px',
  },
  tableContainer: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  tableHeader: {
    background: '#1e293b',
    color: 'white',
  },
  th: {
    padding: '12px 8px',
    textAlign: 'left',
    fontWeight: '600',
    borderBottom: '2px solid #cbd5e1',
  },
  trEven: {
    background: '#f8fafc',
  },
  trOdd: {
    background: 'white',
  },
  td: {
    padding: '10px 8px',
    borderBottom: '1px solid #e2e8f0',
    color: '#475569',
  },
  tdNum: {
    padding: '10px 8px',
    borderBottom: '1px solid #e2e8f0',
    color: '#475569',
    textAlign: 'center',
  },
  tdActions: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    padding: '10px 8px',
    borderBottom: '1px solid #e2e8f0',
    textAlign: 'center',
  },
  badge: {
    display: 'inline-block',
    padding: '4px 12px',
    background: '#dbeafe',
    color: '#1e40af',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
  },
  btnEditar: {
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '5px 10px',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  btnEliminar: {
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '5px 10px',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
};

export default InventarioFisico;